import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'

interface Servizi {
  /** Chiavi dei moduli attivi e non scaduti per il partner corrente. */
  attivi: string[]
  eAttivo: (chiave: string) => boolean
  /** Flag di profilo del partner (0001: partner.venditore / partner.relax). */
  venditore: boolean
  relax: boolean
}

const Ctx = createContext<Servizi | undefined>(undefined)

// Copia dei moduli attivi leggibile fuori da React: serve alle api che girano
// in un handler async (es. generaLinkCliente) senza avere accesso all'hook.
let attiviCorrenti: readonly string[] = []

/** Accesso sincrono ai moduli attivi. Valido finche' ServiziProvider e' montato. */
export function serviziAttiviSincroni(): { eAttivo: (chiave: string) => boolean } {
  return { eAttivo: (chiave) => attiviCorrenti.includes(chiave) }
}

export function ServiziProvider({ children }: { children: ReactNode }) {
  const [attivi, setAttivi] = useState<string[]>([])
  const [profilo, setProfilo] = useState({ venditore: false, relax: false })

  useEffect(() => {
    attiviCorrenti = attivi
  }, [attivi])

  useEffect(() => {
    const oggi = new Date().toISOString().slice(0, 10)
    void supabase
      .from('servizio_attivo')
      .select('chiave')
      .gte('scade_il', oggi)
      .then(({ data }) => setAttivi((data ?? []).map((r) => r.chiave as string)))
    void supabase
      .from('partner')
      .select('venditore, relax')
      .maybeSingle()
      .then(({ data }) => data && setProfilo({ venditore: data.venditore, relax: data.relax }))
  }, [])

  const valore = useMemo<Servizi>(
    () => ({
      attivi,
      eAttivo: (chiave) => attivi.includes(chiave),
      venditore: profilo.venditore,
      relax: profilo.relax,
    }),
    [attivi, profilo],
  )

  return <Ctx.Provider value={valore}>{children}</Ctx.Provider>
}

export function useServizi(): Servizi {
  const c = useContext(Ctx)
  if (!c) throw new Error('useServizi va usato dentro ServiziProvider')
  return c
}
